import { memo } from 'react'

import { Box, Center, Group, Select, Stack, Text } from '@mantine/core'

import { IconArrowDown } from '@repo/common/components/Icons'
import { TokenRender } from '@repo/common/components/TokenRender'
import {
  countFormatter,
  getMaxFractionDigits,
  possiblyUnlimited,
  usdFormatter,
} from '@repo/common/helpers/formatters'
import { merge_mutations } from '@repo/common/helpers/query_helpers'
import type { InSideType } from '@repo/common/queries/fission_dex'
import {
  useAddToLimitOrder,
  useCreateLimitOrder,
} from '@repo/common/queries/limit_orders'
import {
  type FundSymbol,
  swapable_symbols,
} from '@repo/common/queries/products'

import { ContentBox } from '@/components/ContentBox'
import {
  type BasePanelProps,
  classes,
  FlowButton,
  normalize_progress,
  NumberRow,
  RatioAndDisclosures,
  type StepsType,
  useFlowControl,
} from '@/flows/flow_helpers'
import {
  type ExtraAuthSyncingProps,
  PanelAuthSyncing,
  useSkipAuthSyncing,
} from '@/panels/PanelAuthSyncing'
import {
  PanelCheckWalletRisk,
  useSkipWalletRisk,
} from '@/panels/PanelCheckWalletRisk'
import {
  PanelConfirmEmail,
  useSkipConfirmEmail,
} from '@/panels/PanelConfirmEmail'
import {
  PanelLocationAttestation,
  useSkipLocationVerify,
} from '@/panels/PanelLocationAttestation'
import {
  type ExtraPanelPermitProps,
  PanelPermit2,
  useSkipPermit2,
} from '@/panels/PanelPermit2'
import {
  PanelRegisterWallet,
  useRegisterWalletTitle,
  useSkipRegisterWallet,
} from '@/panels/PanelRegisterWallet'
import { PanelSignup, useSkipLogin } from '@/panels/PanelSignup'

import { allowed_kyc_levels, renderSelectOption } from '../Swap'

import { useLimitOrderFields } from './useLimitOrderFields'

type LimitOrderPanelProps = BasePanelProps &
  ExtraAuthSyncingProps &
  ExtraPanelPermitProps

const steps: StepsType<LimitOrderPanelProps> = normalize_progress([
  {
    title: 'Signup',
    Panel: PanelSignup,
    progress: 10,
    useSkipCheck: useSkipLogin,
  },
  {
    title: 'Confirm Email',
    Panel: PanelConfirmEmail,
    progress: 10,
    useSkipCheck: useSkipConfirmEmail,
  },
  {
    title: useRegisterWalletTitle,
    Panel: PanelRegisterWallet,
    progress: 5,
    useSkipCheck: useSkipRegisterWallet,
  },
  {
    title: 'Location Verification',
    Panel: PanelLocationAttestation,
    progress: 5,
    useSkipCheck: useSkipLocationVerify,
  },
  {
    title: 'Wallet Check',
    Panel: PanelCheckWalletRisk,
    progress: 5,
    useSkipCheck: useSkipWalletRisk,
  },
  {
    title: 'Enable Permit2',
    Panel: PanelPermit2,
    progress: 5,
    useSkipCheck: useSkipPermit2,
  },
  {
    title: 'Auth Syncing',
    Panel: PanelAuthSyncing,
    progress: 5,
    useSkipCheck: useSkipAuthSyncing,
  },
])

export const LimitOrder = memo<{
  fund_symbol: FundSymbol
  starting_in_side?: InSideType
  starting_price?: number
  starting_tick?: number
  onCompleted: () => void
  // eslint-disable-next-line max-lines-per-function, complexity
}>(function LimitOrder({
  fund_symbol,
  starting_in_side = 'TOKEN',
  starting_price,
  starting_tick,
  onCompleted,
}) {
  const {
    form,
    wallet_isConnected,
    form_valid,
    paused,
    error_reason,
    price_focus,
    sell_tokens_focus,
    matching_order,
    price_lower,
    price_upper,
    price_tick,
    max_sell_tokens,
    balance_in,
    symbol_out,
    receive_tokens,
    is_loading,
  } = useLimitOrderFields({
    fund_symbol,
    starting_in_side,
    starting_price,
    starting_tick,
  })

  const create_query = useCreateLimitOrder({ fund_symbol })
  const add_query = useAddToLimitOrder({ fund_symbol })

  const action_word = matching_order ? 'Add to Order' : 'Create Limit Order'

  const { submitted, onSubmit, modal } = useFlowControl({
    steps,
    extra_args: {
      symbol_in: form.getValues().symbol_in,
      allowed_kyc_levels,
    },
    final_action: async () => {
      if (matching_order) {
        await add_query.mutateAsync({
          order_token_id: matching_order.id,
          ...form.getValues(),
        })
      } else {
        await create_query.mutateAsync({
          price_tick,
          ...form.getValues(),
        })
      }
      onCompleted()
    },
  })

  return (
    <Stack maw="100%">
      {modal}

      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack
          maw="calc(100vw - 2 * var(--app-body-margin))"
          w="500px"
          gap="xxs"
        >
          <ContentBox
            p="lg"
            gap="xs"
            ref={price_focus.ref}
            mod={{
              focused: price_focus.focused,
              error: Boolean(form.getInputProps('price').error),
            }}
          >
            <Text size="md" lh="1" opacity={0.7} component="label">
              Sell Price
            </Text>
            <NumberRow
              {...form.getInputProps('price', { withError: false })}
              disabled={submitted || is_loading}
              aria-label="sell price"
              allowNegative={false}
              decimalScale={2}
              prefix="$"
              placeholder="$0"
              min={price_lower}
              max={price_upper}
              symbol={
                <Select
                  {...form.getInputProps('symbol_in')}
                  aria-label="sell token"
                  w="125px"
                  size="md"
                  disabled={submitted}
                  data={swapable_symbols}
                  classNames={{
                    input: classes.select,
                    dropdown: classes.select_dropdown,
                  }}
                  allowDeselect={false}
                  renderOption={renderSelectOption}
                  rightSection={<IconArrowDown size="16px" />}
                  leftSection={
                    <TokenRender
                      size="var(--mantine-font-size-xl)"
                      symbol={form.getValues().symbol_in}
                    />
                  }
                />
              }
              details={
                <>
                  {price_upper > 10_000 ? (
                    <>
                      Acceptable range: {usdFormatter(price_lower)} - Very High
                    </>
                  ) : (
                    <>
                      Acceptable range: {usdFormatter(price_lower)} -{' '}
                      {usdFormatter(price_upper)}
                    </>
                  )}
                </>
              }
            />
          </ContentBox>
          <Box pos="relative">
            <Box className={classes.down_box}>
              <Center h="100%">
                <IconArrowDown
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              </Center>
            </Box>
            <ContentBox
              p="lg"
              gap="xs"
              ref={sell_tokens_focus.ref}
              mod={{
                focused: sell_tokens_focus.focused,
                error: Boolean(form.getInputProps('sell_tokens').error),
              }}
              data-testid="sell_container"
            >
              <Text size="md" lh="1" opacity={0.7}>
                Sell{matching_order ? '  Another' : ''}
              </Text>
              <NumberRow
                {...form.getInputProps('sell_tokens', { withError: false })}
                disabled={is_loading || submitted}
                aria-label="sell tokens"
                decimalScale={getMaxFractionDigits(form.getValues().symbol_in)}
                balance_number={balance_in}
                min={0.1}
                max={max_sell_tokens}
                balance_maximumFractionDigits={getMaxFractionDigits(
                  form.getValues().symbol_in,
                )}
                max_button={() => form.setFieldValue('sell_tokens', balance_in)}
                symbol={form.getValues().symbol_in}
                details={
                  <>
                    {matching_order != null && (
                      <>
                        Existing amount:{' '}
                        {countFormatter(matching_order.tokens, {
                          maximumFractionDigits: getMaxFractionDigits(
                            form.getValues().symbol_in,
                          ),
                          compact: false,
                        })}
                      </>
                    )}
                  </>
                }
              />
            </ContentBox>
          </Box>
          <ContentBox p="lg" gap="xs" data-testid="receive_container">
            <Text size="md" lh="1" opacity={0.7}>
              Receive Once Closed
            </Text>
            <NumberRow
              readonly
              value={receive_tokens}
              symbol={symbol_out}
              aria-label={`receive ${symbol_out} tokens`}
              balance_maximumFractionDigits={getMaxFractionDigits(symbol_out)}
            />
          </ContentBox>
          <FlowButton
            wallet_isConnected={wallet_isConnected}
            form_valid={form_valid}
            is_loading={is_loading}
            paused={paused}
            error_reason={error_reason}
            mutation_query={merge_mutations([create_query, add_query])}
            action_word={action_word}
          />
          <RatioAndDisclosures>
            <Group justify="space-between">
              <Text size="sm">
                Sell limit at this price:{' '}
                {possiblyUnlimited(
                  max_sell_tokens,
                  countFormatter(max_sell_tokens, {
                    maximumFractionDigits: getMaxFractionDigits(
                      form.getValues().symbol_in,
                    ),
                    compact: true,
                  }),
                )}
              </Text>
            </Group>
          </RatioAndDisclosures>
        </Stack>
      </form>
    </Stack>
  )
})
